import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import Styles from './ChargingTokenPage.module.css'
import {
  ChargingTokenList,
  ChargingTokenPageTabs,
  ChargingTokenDesignGrid,
} from 'Containers'
import ChargingTokenActions from 'Stores/ChargingToken/Actions'
import ChargingTokenSelectors from 'Stores/ChargingToken/Selectors'
import ChargingTokenDesignSelectors from 'Stores/ChargingTokenDesign/Selectors'
import ChargingTokenDesignActions from 'Stores/ChargingTokenDesign/Actions'
import RequestState from 'Enums/RequestState'
import { IFButton, IFFilter, IFModal, IFSkeleton } from 'Components'
import { useTranslation } from 'react-i18next'
import Colors from 'Theme/Colors'
import AuthSelectors from 'Stores/Auth/Selectors'
import PropTypes from 'prop-types'
import { Scrollbars } from 'react-custom-scrollbars'
import history from 'history/browser'
import { encodeObjectToBase64 } from 'Utils/Base64Functions'
import AddChargingTokenDesignModal from './AddChargingTokenDesignModal'
import CreateChargingToken from './CreateChargingToken'
import VoucherSelectors from 'Stores/Voucher/Selectors'
import VoucherActions from 'Stores/Voucher/Actions'
import InfinityEnums from 'Enums/InfinityEnums'
import LoadingBar from 'react-top-loading-bar'
import { useLocation } from 'react-router-dom'
import { INITIAL_STATE } from 'Stores/ChargingToken/initialState'
import { Actions, Subjects } from 'Enums/permissionKeys'

const ChargingTokenPage = ({
  fetchChargingTokens,
  chargingTokens,
  fetchChargingTokensRequestState,
  loggedInAdmin,
  editingAdminRole,
  chargingTokenFilter,
  allChargingTokenDesigns,
  fetchAllChargingTokenDesigns,
  clearChargingTokens,
  allVouchers,
  fetchAllVouchers,
  ChargingTokenCount,
  getChargingTokensCountRequestState,
  chargingTokenDesign,
  addChargingTokenDesignRequestState,
  fetchChargingTokenDesigns,
  fetchChargingTokenDesignsRequestState,
  chargingTokenDesignCount,
  clearDesigns,
  getChargingTokenDesignsCount,
  editChargingTokenDesignRequestState,
  getChargingTokensCount,
}) => {
  const location = useLocation()
  useEffect(() => {
    if (location.state?.refresh) {
      setFirstLoad(true)

      if (canViewTokens) {
        clearChargingTokens()
        chargingTokenFilterRef?.current?.clearFilter()
      }
      if (canViewDesigns) {
        clearDesigns()
      }
      if (canViewDesigns && canViewTokens) {
        const searchParams = new URLSearchParams(window.location.search)
        searchParams.set('tab', 0)
        searchParams.set(
          'filter',
          encodeObjectToBase64(INITIAL_STATE.chargingTokenFilter),
        )
        history.push({
          pathname: '/chargingTokens',
          search: searchParams.toString(),
        })
      }
      if (canViewTokens) setSelectedTabIndex(0)
      else setSelectedTabIndex(1)
    }
  }, [location.state?.refresh])
  const { t } = useTranslation()
  const chargingTokenFilterRef = useRef(null)
  const [tablesHeight, setTablesHeight] = useState(0)
  const chargingTokentablesRef = useRef(null)
  const createRoleModalRef = useRef()
  const canViewTokens = editingAdminRole?.can(
    Actions.VIEW,
    Subjects.CHARGING_TOKENS,
  )
  const canViewDesigns = editingAdminRole?.can(
    Actions.VIEW,
    Subjects.CHARGING_TOKENS_DESIGN,
  )
  const [selectedTabIndex, setSelectedTabIndex] = useState(() => {
    if (canViewDesigns && canViewTokens) {
      const searchParams = new URLSearchParams(window.location.search)
      const tabParam = parseInt(searchParams.get('tab'), 10)
      if (!isNaN(tabParam) && (tabParam === 0 || tabParam === 1)) {
        return tabParam
      }
      if (canViewTokens) return 0
      if (canViewDesigns) return 1
      return 0
    } else if (canViewTokens) return 0
    else return 1
  })
  const canAddToken = editingAdminRole?.can(
    Actions.CREATE,
    Subjects.CHARGING_TOKENS,
  )
  const canAddDesign = editingAdminRole?.can(
    Actions.CREATE,
    Subjects.CHARGING_TOKENS_DESIGN,
  )
  const [firstLoad, setFirstLoad] = useState(true)
  const [progress, setProgress] = useState(0)
  const addDesignModalRef = useRef()
  useEffect(() => {
    return () => {
      clearChargingTokens()
      setFirstLoad(true)
    }
  }, [])
  useEffect(() => {
    if (
      addChargingTokenDesignRequestState === RequestState.SUCCEEDED ||
      editChargingTokenDesignRequestState === RequestState.SUCCEEDED
    ) {
      if (addDesignModalRef.current) {
        addDesignModalRef.current.dismiss()
      }
    }
  }, [addChargingTokenDesignRequestState, editChargingTokenDesignRequestState])

  useEffect(() => {
    if (selectedTabIndex === 0 && canViewTokens) {
      fetchChargingTokens(0, false)
      fetchAllVouchers(0)
      getChargingTokensCount()
      if (canAddToken) fetchAllChargingTokenDesigns()
      clearDesigns()
      if (canViewDesigns) getChargingTokenDesignsCount()
    } else if (selectedTabIndex === 1 && canViewDesigns) {
      fetchChargingTokenDesigns(0)
      getChargingTokenDesignsCount()
      if (canViewTokens) {
        chargingTokenFilterRef?.current?.clearFilter()
        history.push({
          pathname: '/chargingTokens',
          search: `?filter=${encodeObjectToBase64(
            INITIAL_STATE.chargingTokenFilter,
          )}`,
        })
        getChargingTokensCount()
      }
    }
  }, [selectedTabIndex])

  useEffect(() => {
    const handleResize = (entries) => {
      if (entries[0]) {
        const { height } = entries[0].contentRect
        setTablesHeight(height)
      }
    }

    const resizeObserver = new ResizeObserver(handleResize)

    if (chargingTokentablesRef.current) {
      resizeObserver.observe(chargingTokentablesRef.current)
    }

    return () => {
      if (chargingTokentablesRef.current) {
        resizeObserver.unobserve(chargingTokentablesRef.current)
      }
    }
  })
  useEffect(() => {
    if (canViewDesigns && canViewTokens) {
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.set('tab', selectedTabIndex)
      history.push({
        pathname: '/chargingTokens',
        search: searchParams.toString(),
      })
    }
  }, [selectedTabIndex])

  useEffect(() => {
    if (
      fetchChargingTokensRequestState === RequestState.SUCCEEDED &&
      firstLoad
    ) {
      setFirstLoad(false)
    }
  }, [fetchChargingTokensRequestState, firstLoad])

  useEffect(() => {
    if (
      firstLoad &&
      fetchChargingTokensRequestState === RequestState.LOADING &&
      progress === 0
    ) {
      setProgress(progress + 10)
    }

    if (
      firstLoad &&
      fetchChargingTokensRequestState === RequestState.SUCCEEDED
    ) {
      setProgress(100)
    }
    if (
      firstLoad &&
      fetchChargingTokensRequestState !== RequestState.LOADING &&
      fetchChargingTokensRequestState !== RequestState.UNINITIALIZED &&
      fetchChargingTokensRequestState !== RequestState.SUCCEEDED
    )
      setProgress(100)
  }, [fetchChargingTokensRequestState])

  const handleCreateNewTokenClick = () => {
    createRoleModalRef.current.show()
  }
  const handleCreateNewDesignClick = () => {
    addDesignModalRef.current.show()
  }

  const handleFilterChange = (newFilter) => {
    history.push({
      pathname: '/chargingTokens',
      search: `?filter=${encodeObjectToBase64(newFilter)}`,
    })
    clearChargingTokens()
    fetchChargingTokens(0)
    getChargingTokensCount()
  }
  const handleTabChange = (newValue) => {
    chargingTokenFilterRef?.current?.clearFilter()
    setSelectedTabIndex(newValue)
  }

  return (
    <div className={Styles.Wrapper}>
      <div>
        <LoadingBar
          color={Colors.primary}
          progress={progress}
          onLoaderFinished={() => setProgress(0)}
        />
      </div>
      <div
        className={Styles.ChargingTokenContainer}
        style={{
          backgroundColor: Colors.white,
          borderColor: Colors.UserPageBackgroundContainer,
        }}
      >
        <div className={Styles.TabButtonDiv}>
          <ChargingTokenPageTabs
            editingAdminRole={editingAdminRole}
            onValueChange={handleTabChange}
            initialIndex={selectedTabIndex}
            isLoading={
              (fetchChargingTokensRequestState === RequestState.LOADING ||
                fetchChargingTokenDesignsRequestState ===
                  RequestState.LOADING) &&
              firstLoad
            }
            ChargingTokenCount={ChargingTokenCount}
            ChargingTokenDesignCount={chargingTokenDesignCount}
            fetchListRequestState={getChargingTokensCountRequestState}
          />

          {
            <div className={Styles.AddButtonDiv}>
              {firstLoad &&
              (fetchChargingTokensRequestState === RequestState.LOADING ||
                fetchChargingTokenDesignsRequestState ===
                  RequestState.LOADING) ? (
                <IFSkeleton variant="text" width="100px" />
              ) : (
                <>
                  {selectedTabIndex === 0
                    ? canAddToken && (
                        <IFButton
                          size="sm"
                          color={Colors.primary}
                          isFill={false}
                          isLoading={false}
                          text={t('ChargingTokenPage.CreateChargingToken')}
                          className={Styles.AddTokenButton}
                          onClick={handleCreateNewTokenClick}
                        />
                      )
                    : canAddDesign && (
                        <IFButton
                          size="sm"
                          color={Colors.primary}
                          isFill={false}
                          isLoading={false}
                          text={t('ChargingTokenPage.CreateDesign')}
                          className={Styles.AddTokenButton}
                          onClick={handleCreateNewDesignClick}
                        />
                      )}
                </>
              )}
            </div>
          }
        </div>
        {selectedTabIndex === 0 && (
          <div className={Styles.FilterDiv}>
            <IFFilter
              ref={chargingTokenFilterRef}
              onFilterChange={(newFilter) => handleFilterChange(newFilter)}
              filters={chargingTokenFilter}
              textFieldPlaceholder={t(
                'ChargingTokenPage.ChargingTokenFilterPlaceholder',
              )}
              isLoading={
                fetchChargingTokensRequestState === RequestState.LOADING &&
                firstLoad
              }
            />
          </div>
        )}

        <Scrollbars className={Styles.ScrollParent}>
          {selectedTabIndex === 0 ? (
            <div className={Styles.Tables} ref={chargingTokentablesRef}>
              <ChargingTokenList
                tablesHeight={tablesHeight}
                chargingTokens={chargingTokens}
                loggedInAdmin={loggedInAdmin}
                editingAdminRole={editingAdminRole}
                allChargingTokenDesigns={allChargingTokenDesigns}
                allVouchers={allVouchers}
              />
            </div>
          ) : (
            <>
              <ChargingTokenDesignGrid
                chargingTokenDesigns={chargingTokenDesign}
                fetchChargingTokenDesignsRequestState={
                  fetchChargingTokenDesignsRequestState
                }
              />
            </>
          )}
        </Scrollbars>

        <CreateChargingToken
          ref={createRoleModalRef}
          allChargingTokenDesigns={allChargingTokenDesigns}
          allVouchers={allVouchers}
        />
      </div>
      <IFModal
        title={t('ChargingTokenPage.AddChargingTokenDesign')}
        ref={addDesignModalRef}
      >
        <AddChargingTokenDesignModal />
      </IFModal>
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchChargingTokens: (offset, shouldShowError) =>
      dispatch(
        ChargingTokenActions.fetchChargingTokens(offset, shouldShowError),
      ),
    fetchAllChargingTokenDesigns: (offset, designName) =>
      dispatch(
        ChargingTokenDesignActions.fetchAllChargingTokenDesigns(
          offset,
          designName,
        ),
      ),
    clearChargingTokens: () =>
      dispatch(ChargingTokenActions.clearChargingTokens()),
    fetchAllVouchers: (offset, voucherName) =>
      dispatch(VoucherActions.fetchAllVouchers(offset, voucherName)),
    getChargingTokensCount: () =>
      dispatch(ChargingTokenActions.getChargingTokensCount()),
    fetchChargingTokenDesigns: (offset) =>
      dispatch(ChargingTokenDesignActions.fetchChargingTokenDesigns(offset)),
    getChargingTokensCount: () =>
      dispatch(ChargingTokenActions.getChargingTokensCount()),
    getChargingTokenDesignsCount: () =>
      dispatch(ChargingTokenDesignActions.getChargingTokenDesignsCount()),
    clearDesigns: () => dispatch(ChargingTokenDesignActions.clearDesigns()),
  }
}

const mapStateToProps = (state) => ({
  fetchChargingTokensRequestState:
    ChargingTokenSelectors.getFetchChargingTokensRequestState(state),
  chargingTokens: ChargingTokenSelectors.getChargingTokens(state),
  paginationOffset: ChargingTokenSelectors.getPaginationOffset(state),
  loggedInAdmin: AuthSelectors.getUser(state),
  chargingTokenFilter: ChargingTokenSelectors.getChargingTokenFilter(state),
  allChargingTokenDesigns:
    ChargingTokenDesignSelectors.getAllChargingTokenDesigns(state),
  allVouchers: VoucherSelectors.getAllVouchers(state),
  ChargingTokenCount: ChargingTokenSelectors.getChargingTokenCount(state),
  getChargingTokensCountRequestState:
    ChargingTokenSelectors.getChargingTokensCountRequestState(state),
  editingAdminRole: AuthSelectors.getEditingAdminRole(state),
  addChargingTokenDesignRequestState:
    ChargingTokenDesignSelectors.getAddChargingTokenDesignRequestState(state),
  editChargingTokenDesignRequestState:
    ChargingTokenDesignSelectors.getEditChargingTokenDesignRequestState(state),
  chargingTokenDesign:
    ChargingTokenDesignSelectors.getChargingTokenDesigns(state),
  fetchChargingTokenDesignsRequestState:
    ChargingTokenDesignSelectors.getFetchChargingTokenDesignsRequestState(
      state,
    ),
  chargingTokenDesignCount:
    ChargingTokenDesignSelectors.getAppChargingDesignsTokenCount(state),
  getChargingTokenCountRequestState:
    ChargingTokenDesignSelectors.getGetChargingTokenCountRequestState(state),
})

ChargingTokenPage.propTypes = {
  fetchChargingTokens: PropTypes.func,
  chargingTokens: PropTypes.array,
  fetchChargingTokensRequestState: PropTypes.string,
  loggedInAdmin: PropTypes.object,
  editingAdminRole: PropTypes.object,
  chargingTokenFilter: PropTypes.object,
  allChargingTokenDesigns: PropTypes.array,
  fetchAllChargingTokenDesigns: PropTypes.func,
  clearChargingTokens: PropTypes.func,
  allVouchers: PropTypes.array,
  fetchAllVouchers: PropTypes.func,
  ChargingTokenCount: PropTypes.number,
  getChargingTokensCountRequestState: PropTypes.string,
  chargingTokenDesign: PropTypes.array,
  addChargingTokenDesignRequestState: PropTypes.string,
  fetchChargingTokenDesigns: PropTypes.func,
  fetchChargingTokenDesignsRequestState: PropTypes.string,
  chargingTokenDesignCount: PropTypes.number,
  getChargingTokenDesignsCount: PropTypes.func,
  clearDesigns: PropTypes.func,
  editChargingTokenDesignRequestState: PropTypes.string,
}

export default connect(mapStateToProps, mapDispatchToProps)(ChargingTokenPage)
